import React, { useState } from 'react';
import Button from '../../../components/common/Button/Button';

const JobForm = ({
  formData,
  setFormData,
  editingJob,
  handleFormSubmit,
  resetForm,
  setShowJobForm,
  setEditingJob,
  addRequirement,
  removeRequirement,
  updateRequirement,
  addResponsibility,
  removeResponsibility,
  updateResponsibility
}) => {
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle different input types
    let processedValue = value;

    // Text fields: only allow alphanumeric, spaces, and commas
    if (name === 'title' || name === 'experience' || name === 'salary' || name === 'description') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (name === 'title' && processedValue.length > 120) {
        processedValue = processedValue.slice(0, 120);
      } else if (name === 'description' && processedValue.length > 2000) {
        processedValue = processedValue.slice(0, 2000);
      } else if (name === 'experience' && processedValue.length > 50) {
        processedValue = processedValue.slice(0, 50);
      } else if (name === 'salary' && processedValue.length > 50) {
        processedValue = processedValue.slice(0, 50);
      }
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleInputKeyPress = (e) => {
    const { name } = e.target;

    // Text fields: only allow alphanumeric, spaces, and commas
    if (name === 'title' || name === 'experience' || name === 'salary' || name === 'description') {
      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;

    // Validate individual field on blur
    let error = '';

    switch (name) {
      case 'title':
        if (!value.trim()) error = 'Job title is required.';
        else if (value.trim().length < 3) error = 'Job title must be at least 3 characters.';
        break;
      case 'department':
        if (!value.trim()) error = 'Department is required.';
        break;
      case 'location':
        if (!value.trim()) error = 'Location is required.';
        break;
      case 'type':
        if (!value.trim()) error = 'Employment type is required.';
        break;
      case 'closingDate':
        if (!value.trim()) error = 'Application deadline is required.';
        else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            error = 'Application deadline cannot be in the past.';
          }
        }
        break;
      case 'description':
        if (!value.trim()) error = 'Job description is required.';
        else if (value.trim().length < 50) error = 'Job description must be at least 50 characters.';
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required.';
    } else if (formData.title.length > 120) {
      newErrors.title = 'Job title must be ≤ 120 characters.';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.title)) {
      newErrors.title = 'Job title can only contain alphanumeric characters, spaces, and commas.';
    }

    if (!formData.department.trim()) newErrors.department = 'Department is required.';
    if (!formData.location.trim()) newErrors.location = 'Location is required.';
    if (!formData.type.trim()) newErrors.type = 'Employment type is required.';

    if (!formData.closingDate.trim()) newErrors.closingDate = 'Application deadline is required.';
    else {
      const selectedDate = new Date(formData.closingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.closingDate = 'Application deadline cannot be in the past.';
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required.';
    } else if (formData.description.length > 2000) {
      newErrors.description = 'Job description must be ≤ 2000 characters.';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.description)) {
      newErrors.description = 'Job description can only contain alphanumeric characters, spaces, and commas.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    handleFormSubmit(e);
  };
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          {editingJob ? 'Edit Job Posting' : 'Create New Job Posting'}
        </h3>
        <Button
          variant="outline"
          onClick={() => {
            setShowJobForm(false);
            setEditingJob(null);
            resetForm();
          }}
        >
          Cancel
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
              onBlur={handleInputBlur}
              maxLength={120}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              placeholder="Enter job title"
              required
            />
            <div className="flex justify-between items-center mt-1">
              {errors.title && (
                <p className="text-sm text-red-600">{errors.title}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.title.length}/120 characters
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department *
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, department: e.target.value }));
                if (errors.department) {
                  setErrors(prev => ({ ...prev, department: '' }));
                }
              }}
              onBlur={handleInputBlur}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.department ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="">Select Department</option>
              <option value="IT Department">IT Department</option>
              <option value="HR Department">HR Department</option>
              <option value="Finance">Finance</option>
              <option value="Sales Department">Sales Department</option>
              <option value="Marketing">Marketing</option>
              <option value="Operations">Operations</option>
            </select>
            {errors.department && (
              <p className="mt-1 text-sm text-red-600">{errors.department}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location *
            </label>
            <select
              name="location"
              value={formData.location}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, location: e.target.value }));
                if (errors.location) {
                  setErrors(prev => ({ ...prev, location: '' }));
                }
              }}
              onBlur={handleInputBlur}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.location ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="">Select Location</option>
              <option value="Mumbai Office">Mumbai Office</option>
              <option value="Delhi Office">Delhi Office</option>
              <option value="Bangalore Office">Bangalore Office</option>
              <option value="Chennai Office">Chennai Office</option>
              <option value="Pune Office">Pune Office</option>
              <option value="Remote">Remote</option>
            </select>
            {errors.location && (
              <p className="mt-1 text-sm text-red-600">{errors.location}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employment Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, type: e.target.value }));
                if (errors.type) {
                  setErrors(prev => ({ ...prev, type: '' }));
                }
              }}
              onBlur={handleInputBlur}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.type ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Experience Required
            </label>
            <input
              type="text"
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
              maxLength={50}
              placeholder="e.g., 2 4 years"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.experience.length}/50 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Salary Range
            </label>
            <input
              type="text"
              name="salary"
              value={formData.salary}
              onChange={handleInputChange}
              onKeyPress={handleInputKeyPress}
              maxLength={50}
              placeholder="e.g., 500000, 800000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.salary.length}/50 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application Deadline *
            </label>
            <input
              type="date"
              name="closingDate"
              value={formData.closingDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                const value = e.target.value;
                // Validate date to prevent runtime errors
                if (value) {
                  const selectedDate = new Date(value);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  if (selectedDate < today) {
                    setErrors(prev => ({ ...prev, closingDate: 'Application deadline cannot be in the past.' }));
                    return;
                  }
                }

                setFormData(prev => ({ ...prev, closingDate: value }));
                if (errors.closingDate) {
                  setErrors(prev => ({ ...prev, closingDate: '' }));
                }
              }}
              onBlur={handleInputBlur}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.closingDate ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              required
            />
            {errors.closingDate && (
              <p className="mt-1 text-sm text-red-600">{errors.closingDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Urgency Level
            </label>
            <select
              value={formData.urgency}
              onChange={(e) => setFormData(prev => ({ ...prev, urgency: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            onKeyPress={handleInputKeyPress}
            onBlur={handleInputBlur}
            rows={4}
            maxLength={2000}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
              }`}
            placeholder="Describe the role, company culture, and what makes this opportunity exciting..."
            required
          />
          <div className="flex justify-between items-center mt-1">
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {formData.description.length}/2000 characters
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Requirements
          </label>
          {formData.requirements.map((req, index) => (
            <div key={index} className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={req}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                  if (value.length <= 200) {
                    updateRequirement(index, value);
                  }
                }}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={200}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter requirement..."
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeRequirement(index)}
                disabled={formData.requirements.length === 1}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRequirement}
          >
            Add Requirement
          </Button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Responsibilities
          </label>
          {formData.responsibilities.map((resp, index) => (
            <div key={index} className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={resp}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                  if (value.length <= 200) {
                    updateResponsibility(index, value);
                  }
                }}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={200}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter responsibility..."
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeResponsibility(index)}
                disabled={formData.responsibilities.length === 1}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addResponsibility}
          >
            Add Responsibility
          </Button>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowJobForm(false);
              setEditingJob(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            {editingJob ? 'Update Job' : 'Create Job'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
